import { useEffect, useState } from "react";
import { Trash2, Save, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import {
  adminListPanchangCities, adminCreatePanchangCity,
  adminUpdatePanchangCity, adminDeletePanchangCity,
} from "@/lib/api";

const BLANK = { slug: "", name: "", region: "", country: "", lat: "", lon: "", tz: 330, tz_id: "Asia/Kolkata", order: 0, hidden: false };

export default function AdminPanchangCities() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);

  const load = () => adminListPanchangCities().then((d) => setList(d.cities || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("City name is required");
    const payload = {
      ...form,
      lat: parseFloat(form.lat) || 0,
      lon: parseFloat(form.lon) || 0,
      tz: parseInt(form.tz, 10) || 0,
      order: parseInt(form.order, 10) || 0,
    };
    try {
      if (editing) { await adminUpdatePanchangCity(editing, payload); toast.success("City updated"); }
      else { await adminCreatePanchangCity(payload); toast.success("City added — page is now live & indexed"); }
      setForm(BLANK); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed — are you logged in as admin?"); }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({ slug: c.slug || "", name: c.name || "", region: c.region || "", country: c.country || "", lat: c.lat ?? "", lon: c.lon ?? "", tz: c.tz ?? 330, tz_id: c.tz_id || "Asia/Kolkata", order: c.order ?? 0, hidden: !!c.hidden });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const toggleHidden = async (c) => { await adminUpdatePanchangCity(c.id, { hidden: !c.hidden }); load(); };
  const del = async (c) => { if (!window.confirm(`Delete ${c.name}? Its SEO page will be removed.`)) return; await adminDeletePanchangCity(c.id); load(); };

  const F = (k, ph, type = "text") => (
    <input type={type} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} data-testid={`city-${k}`} className="w-full rounded-lg border px-3 py-2 text-sm" />
  );

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="admin-panchang-cities">
      <h1 className="mb-1 text-2xl font-semibold">City Panchangam Pages</h1>
      <p className="mb-6 text-sm text-gray-500">
        {list.length} cities. Each publishes a public, SEO-indexed daily Panchangam at
        <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5">/panchangam/&lt;slug&gt;</code>
        computed live in the city's own timezone (auto year rollover). New cities are added to the sitemap automatically. Choghadiya & the scriptural almanac stay premium-only.
      </p>

      <div className="mb-8 space-y-3 rounded-xl border bg-white p-4">
        <h2 className="font-medium">{editing ? "Edit city" : "Add a city"}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {F("name", "City name e.g. Mumbai")}
          {F("slug", "URL slug e.g. mumbai (auto from name if blank)")}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {F("region", "Region/State e.g. Maharashtra")}
          {F("country", "Country e.g. India")}
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          {F("lat", "Latitude", "number")}
          {F("lon", "Longitude", "number")}
          {F("tz", "UTC offset (mins) e.g. 330", "number")}
          {F("order", "Order", "number")}
        </div>
        {F("tz_id", "IANA timezone e.g. Asia/Kolkata")}
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={form.hidden} onChange={(e) => setForm((f) => ({ ...f, hidden: e.target.checked }))} data-testid="city-hidden" />
          Hidden (not public, not in sitemap)
        </label>
        <div className="flex gap-2">
          <button onClick={save} data-testid="city-save" className="flex items-center gap-1.5 rounded-lg bg-[var(--saffron,#c1440e)] px-4 py-2 text-sm font-medium text-white"><Save className="h-4 w-4" />{editing ? "Update city" : "Add city"}</button>
          {editing && <button onClick={() => { setForm(BLANK); setEditing(null); }} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>}
        </div>
      </div>

      <div className="space-y-2">
        {list.map((c) => (
          <div key={c.id} data-testid={`admin-city-${c.slug}`} className="flex items-center justify-between gap-3 rounded-lg border bg-white p-3">
            <div className="min-w-0">
              <p className="font-medium">
                {c.name}
                {c.hidden && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-500">hidden</span>}
              </p>
              <p className="text-xs text-gray-500">
                {[c.region, c.country].filter(Boolean).join(", ")} · {c.lat}, {c.lon} · {c.tz_id} · /panchangam/{c.slug}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <a href={`/panchangam/${c.slug}`} target="_blank" rel="noopener noreferrer" className="rounded border px-2 py-1 text-xs" title="View page"><ExternalLink className="h-3.5 w-3.5" /></a>
              <button onClick={() => toggleHidden(c)} className="rounded border px-2 py-1 text-xs" title={c.hidden ? "Show" : "Hide"} data-testid={`toggle-${c.slug}`}>{c.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
              <button onClick={() => edit(c)} className="rounded border px-2 py-1 text-xs" data-testid={`edit-${c.slug}`}>Edit</button>
              <button onClick={() => del(c)} data-testid={`del-${c.slug}`} className="rounded border border-red-200 px-2 py-1 text-xs text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
